import { Pad } from 'component/basics';
import { PollPicker, PollResult, PollSurvey, PollSurveyThenResults } from 'component/poll';
import { UtilityText } from 'component/text';
import { useState } from 'react';
import { View } from 'react-native';
import { CLICK, component, componentWithVariants, demoFile, INPUT, story, variant } from 'system/demo';
import { t, undefB, undefS, undefSL, undefTLS } from 'util/stdtypes';

demoFile('component/poll', {
    designUrl: 'https://www.figma.com/design/MX0AcO8d0ZlCBs4e9vkl5f/PSI-Design-System?node-id=4831-5007&t=t6FrFrD5ap18phaP-0',
});

const commentSliderLabels = [
    t('Strongly no'),
    t('No with reservations'),
    t("It's complicated"),
    t('Yes with reservations'),
    t('Strongly yes'),
];

componentWithVariants('PollResult', PollResult, {}, [
    variant('With option Labels', {
        args: {
            optionLabels: commentSliderLabels,
            votes: [
                'Strongly no',
                'No with reservations',
                'No with reservations',
                "It's complicated",
                'Yes with reservations',
                'Strongly yes',
            ],
            myVote: 'Strongly no',
        },
    }),
    variant('With Option Texts', {
        args: {
            optionTexts: ['Cat', 'Dog', 'Komodo Dragon'],
            votes: ['Cat', 'Cat', 'Cat'],
            myVote: 'Cat',
        },
    }),
]);

function PollSurveyThenResultsDemo({
    question = undefS,
    optionLabels = undefTLS,
    optionTexts = undefSL,
    votes = undefSL,
    showOtherOption = undefB,
    showButton = undefB,
    otherOptionText = undefS,
}) {
    const [myVote, setMyVote] = useState(undefined);
    const [myComment, setMyComment] = useState(null);

    function onVote(option, comment) {
        setMyVote(option);
        setMyComment(comment);
    }

    return (
        <View>
            <PollSurveyThenResults
                myComment={myComment}
                myVote={myVote}
                onVote={onVote}
                optionLabels={optionLabels}
                optionTexts={optionTexts}
                otherOptionPlaceholder={t('!Enter your choice')}
                otherOptionText={otherOptionText}
                question={question}
                showButton={showButton}
                showOtherOption={showOtherOption}
                votes={votes}
            />
            <Pad />
            <UtilityText text={myVote ? 'You voted for ' + myVote : 'You have not voted yet'} />
            {myComment && <UtilityText text={myComment} />}
        </View>
    );
}

componentWithVariants('PollSurveyThenResults', PollSurveyThenResultsDemo, {}, [
    variant(
        null,
        {
            args: {
                question: 'What is your favorite animal?',
                optionTexts: ['Cat', 'Dog', 'Komodo Dragon'],
                showButton: false,
                votes: ['Cat', 'Cat', 'Dog', 'Dog', 'Dog', 'Komodo Dragon'],
            },
        },
        [story('Cat', [CLICK('Cat')]), story('Cat then Dog', [CLICK('Cat'), CLICK('Change my vote'), CLICK('Dog')])],
    ),

    variant(
        'Other Option and Button',
        {
            args: {
                question: 'What is your favorite animal?',
                optionTexts: ['Cat', 'Dog', 'Komodo Dragon'],
                showOtherOption: true,
                showButton: true,
                otherOptionText: 'Other',
                votes: ['Cat', 'Cat', 'Dog', 'Dog', 'Dog', 'Komodo Dragon'],
            },
        },
        [
            story('Cat', [CLICK('Cat'), CLICK('Submit my vote')]),
            story('Cat then Dog', [CLICK('Cat'), CLICK('Submit my vote'), CLICK('Change my vote'), CLICK('Dog'), CLICK('Submit my vote')]),
            story('Other', [CLICK('Other'), INPUT('other-option', 'Badger'), CLICK('Submit my vote')]),
        ],
    ),
]);

function PollPickerDemo() {
    const [value, setValue] = useState<number | null>(null);
    return (
        <PollPicker
            onChangeValue={setValue}
            optionColors={['red', 'orange', 'yellow', 'green', 'blue']}
            optionLabels={[
                t('Strongly no'),
                t('No with reservations'),
                t("It's complicated"),
                t('Yes with reservations'),
                t('Strongly yes'),
            ]}
            placeholder={t('!Are cats cute?')}
            value={value}
        />
    );
}

component('PollPicker', PollPickerDemo, {}, [
    story('Strongly no', [CLICK('Strongly no')]),
    story('No then yes', [CLICK('No with reservations'), CLICK('Change'), CLICK('Strongly yes')]),
]);

function PollSurveyDemo({
    optionTexts,
    showButton = true,
    showOtherOption = false,
    otherOptionLabel = t('Other'),
    otherOptionPlaceholder = t('Write in a topic...'),
}) {
    const [myVote, setMyVote] = useState(undefined);
    const [comment, setComment] = useState(null);

    function onVote(option, comment) {
        setMyVote(option);
        setComment(comment);
    }

    return (
        <View>
            <PollSurvey
                myVote={myVote}
                onVote={onVote}
                optionTexts={optionTexts}
                otherOptionLabel={otherOptionLabel}
                otherOptionPlaceholder={otherOptionPlaceholder}
                showButton={showButton}
                showOtherOption={showOtherOption}
            />
            <Pad />
            <UtilityText text={myVote ? 'You voted for ' + myVote : 'You have not voted yet'} />
            {comment && <UtilityText text={comment} />}
        </View>
    );
}

const optionTexts = ['Cat', 'Dog', 'Komodo Dragon'];

componentWithVariants('PollSurvey', PollSurveyDemo, {}, [
    variant('with Button', { args: { optionTexts } }, [
        story('Cat', [CLICK('Cat'), CLICK('Submit my vote')]),
        story('Cat then Dog', [CLICK('Cat'), CLICK('Dog'), CLICK('Submit my vote')]),
    ]),

    variant(
        'without Button',
        {
            args: { showButton: false, optionTexts },
        },
        [story('Cat', [CLICK('Cat')]), story('Cat then Dog', [CLICK('Cat'), CLICK('Dog')])],
    ),

    variant('with Other Option', {
        args: { showOtherOption: true, optionTexts },
    }),
]);
